import { KnightGame } from './KnightGame.js';
import { KnightRenderer } from './KnightRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Position } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, TraceStep } from '../../input/KeyboardController.js';

export class KnightInput extends BaseInput<KnightGame, KnightRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: KnightGame,
        renderer: KnightRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const hit = this.detector.detectCell(x, y);
        if (hit && this.game.removeSegmentsAtCell({ row: hit.row, col: hit.col })) {
            this.onUpdate();
        }
    };

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);
        const hit = this.detector.detectCell(x, y);

        if (!hit) return;

        if (this.gateMode && isVortexGame(this.game)) {
            if (this.game.toggleVortexGate(hit)) {
                this.onUpdate();
            }
        } else {
            if (this.game.handleClick(hit)) {
                this.onUpdate();
            }
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Place = click (select / extend / backtrack); delete = remove segments here.

    placeAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        return this.game.handleClick(pos);
    }

    deleteAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        return this.game.removeSegmentsAtCell(pos);
    }

    /** A trace begins by selecting the anchor cell (as a click on it would). */
    traceStart(pos: Position): void {
        if (this.gateMode) return;
        this.game.selectCell(pos);
    }

    /** While tracing, each cell the cursor reaches that's a reachable knight cell
     *  (extend, join, or backtrack) snaps in a segment and re-anchors there — just
     *  like clicking it. Intermediate cells the cursor crosses do nothing. */
    traceStep({ to }: TraceStep): boolean {
        if (this.gateMode) return false;
        if (!this.game.getSelectedCell()) return false;
        const reachable = this.game.getAccessibleCells()
            .some(c => c.row === to.row && c.col === to.col);
        return reachable ? this.game.handleClick(to) : false;
    }

    /** `X` goes to the first digit — where a solution has to start. */
    cursorOrigin(): Position | null {
        const grid = this.game.getGrid();
        return grid.getDigitPosition(grid.minDigit);
    }
}
